import { getBinanceBaseUrls } from '@/lib/binanceBaseUrls';

import type {
  T_CandlePoint,
  T_LinePoint,
  T_Symbol,
  T_Timeframe
} from '@/ts/types/T_Chart';

export const BINANCE_SYMBOL: Record<T_Symbol, string> = {
  BTC: 'BTCUSDT',
  ETH: 'ETHUSDT',
  SOL: 'SOLUSDT',
  XRP: 'XRPUSDT'
};

/**
 * Converts a symbol string to Binance pair format (e.g., "BTC" -> "BTCUSDT")
 * Falls back to the hardcoded mapping if available, otherwise constructs dynamically
 */
export function getBinancePair(symbol: string): string {
  const upperSymbol = symbol.toUpperCase();
  // Check if it's in the hardcoded mapping first
  if (
    upperSymbol in BINANCE_SYMBOL &&
    BINANCE_SYMBOL[upperSymbol as T_Symbol]
  ) {
    return BINANCE_SYMBOL[upperSymbol as T_Symbol];
  }
  // Otherwise, construct dynamically (e.g., "DOGE" -> "DOGEUSDT")
  return `${upperSymbol}USDT`;
}

function binanceInterval(tf: T_Timeframe): string {
  switch (tf) {
    case '1H':
      return '1m';
    case '6H':
      return '5m';
    case '12H':
      return '15m';
    case '1D':
      return '1h';
    case '1M':
      return '1m';
    case 'ALL':
      return '1m';
  }
}

function limitFor(tf: T_Timeframe): number {
  switch (tf) {
    case '1H':
      return 60;
    case '6H':
      return (6 * 60) / 5;
    case '12H':
      return (12 * 60) / 15;
    case '1D':
      return 24;
    case '1M':
      return 30;
    case 'ALL':
      return 365; // 1y daily
  }
}

export async function fetchBinance(
  symbol: T_Symbol | string,
  tf: T_Timeframe
): Promise<T_CandlePoint[] | T_LinePoint[]> {
  const pair = getBinancePair(symbol);
  const interval = binanceInterval(tf);
  const limit = limitFor(tf);

  const path = `/api/v3/klines?symbol=${pair}&interval=${interval}&limit=${limit}`;
  let lastError: unknown;
  let res: Response | null = null;

  const init: RequestInit = {
    next: { revalidate: 1 }
  };

  for (const baseUrl of getBinanceBaseUrls()) {
    try {
      res = await fetch(`${baseUrl}${path}`, init);
      if (res.ok) break;
      lastError = new Error(`Binance ${res.status} from ${baseUrl}`);
    } catch (error) {
      lastError = error;
    }
  }

  if (!res || !res.ok) {
    throw lastError ?? new Error('Failed to fetch Binance klines');
  }

  const rows: Array<
    [
      number,
      string,
      string,
      string,
      string,
      string,
      number,
      string,
      number,
      string,
      string,
      string
    ]
  > = await res.json();

  return rows.map(k => ({
    time: Math.floor(k[0] / 1000), // openTime -> seconds
    open: Number(k[1]),
    high: Number(k[2]),
    low: Number(k[3]),
    close: Number(k[4])
  }));
}
